import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, TouchableHighlight, View } from 'react-native';
import { useHeaderHeight } from '@react-navigation/elements';
import { applyToSwitching } from 'actions';
import { AppView, Button, rem, ScrollView } from 'design-system';
import Amount from 'design-system/Amount';
import { HEIGHT } from 'design-system/Button';
import { IconBoldNote, IconCoinStack, IconMoneyBills, } from 'design-system/icons';
import IconBoldSaved from 'design-system/icons/IconBoldSaved';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useSharedValue } from 'react-native-reanimated';
import { useAppDispatch } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import Header from '../../broadband/Header';
import Row from '../../broadband/Row';
import QuoteDetailsCard from '../../QuoteDetailsCard';
const shadowOpacityOutputRange = [0, 0, 1, 1];
const EnergyQuote = ({ navigation, route }) => {
    const scrollY = useSharedValue(0);
    const dispatch = useAppDispatch();
    const colors = useColors();
    const paddingBottom = useMarginBottom('paddingBottom', rem(8) + HEIGHT);
    const headerHeight = useHeaderHeight();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        Amplitude.logEvent('MoneySuperMarket.Quote');
    }, []);
    const { data } = route.params;
    const { tariff } = data.data;
    const { projection, supplier } = data.data;
    const { estimatedSaving, annualBill, paymentValue } = projection;
    const { name, summary, rateType, onlineApplicationPermitted } = tariff || {};
    const expandedHeaderHeight = onlineApplicationPermitted ? rem(278) : rem(238);
    const animationInputRange = useMemo(() => [
        0,
        expandedHeaderHeight - headerHeight,
        expandedHeaderHeight,
        expandedHeaderHeight + 1,
    ], [expandedHeaderHeight, headerHeight]);
    const bgColorOutputRange = useMemo(() => [
        colors.background.dark,
        colors.background.dark,
        colors.cards.onDark,
        colors.cards.onDark,
    ], [colors.background.dark, colors.cards.onDark]);
    if (!tariff || !name)
        return null;
    const onSwitchNow = () => {
        const { enquiryId } = route.params;
        Promise.resolve(dispatch(applyToSwitching(enquiryId, data.id))).then((response) => {
            if (response?.payload?.webview?.url) {
                navigation.navigateDeprecated('EnergyApplication', {
                    data: response.payload,
                    name: data.data.supplier.name,
                });
            }
            return response;
        });
    };
    const onSwitchNowBottom = () => {
        Amplitude.logEvent('MoneySuperMarket.QuoteClickSwitch', {
            position: 'bottom',
        });
        onSwitchNow();
    };
    const onSwitchNowCenter = () => {
        Amplitude.logEvent('MoneySuperMarket.QuoteClickSwitch', {
            position: 'center',
        });
        onSwitchNow();
    };
    const onPressInformation = () => {
        const { informationLabel } = tariff;
        navigation.navigateDeprecated('InformationLabel', { informationLabel });
    };
    const onPressCompare = () => {
        const { comparison } = tariff;
        navigation.navigateDeprecated('QuoteFurtherInformation', { comparison });
    };
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <ScrollView secondary scrollY={scrollY} bgColorOutputRange={bgColorOutputRange} animationInputRange={animationInputRange} contentContainerStyle={paddingBottom} shadowOpacityOutputRange={shadowOpacityOutputRange} expandedHeaderHeight={expandedHeaderHeight + headerHeight}>
        <View>
          <QuoteDetailsCard title="Plan Summary">
            <Row title="Annual Bill" value={`£${annualBill}`} Icon={IconMoneyBills}/>
            <Row title="Payment Value" value={`£${paymentValue}`} Icon={IconCoinStack} gradientKey="yellow"/>
            <Row title="Rate type" value={rateType} Icon={IconBoldNote} gradientKey="turquoise"/>
          </QuoteDetailsCard>
          {tariff.features !== undefined && tariff.features.length > 0 && (<QuoteDetailsCard title="Tariff Features">
              {tariff.features.map((feature) => (<Row title={feature.description} key={feature.sortOrder} Icon={IconBoldSaved}/>))}
            </QuoteDetailsCard>)}
          <QuoteDetailsCard title="Tarrif Summary">
            <View style={styles.summaryCard}>
              <Text Text-16>{summary}</Text>
              <TouchableHighlight underlayColor={colors.background.underlay} activeOpacity={1} onPress={onPressInformation} style={styles.link}>
                <Text TextThin-14 Primary>
                  See full Tariff Information Label breakdown
                </Text>
              </TouchableHighlight>
              <TouchableHighlight underlayColor={colors.background.underlay} activeOpacity={1} onPress={onPressCompare} style={styles.link}>
                <Text TextThin-14 Primary>
                  How does this compare to my existing tariff?
                </Text>
              </TouchableHighlight>
            </View>
          </QuoteDetailsCard>
        </View>
      </ScrollView>
      <Header logoUrl={supplier.logoUrl} scrollY={scrollY} title={supplier.name} subtitle={name} buttonText="Switch now" onPress={onlineApplicationPermitted ? onSwitchNowCenter : undefined}>
        <Amount Numbers-16 value={{ currency: 'GBP', value: estimatedSaving }}/>
        <Text TextThin-14 Secondary style={styles.savingsValue}>
          Estimated yearly saving
        </Text>
      </Header>
      {Boolean(onlineApplicationPermitted) && (<Button brand floating onPress={onSwitchNowBottom} title="Switch now"/>)}
    </AppView>);
};
const styles = StyleSheet.create({
    savingsValue: {
        marginBottom: rem(16),
    },
    summaryCard: {
        padding: rem(16),
    },
    link: {
        marginTop: rem(16),
    },
});
export default EnergyQuote;
